import React from 'react';
import { Tag, Drawer, Descriptions, Typography, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { CommunicationLogDto } from '../api/communicationsApi';

const { Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  sent: 'green',
  failed: 'red',
  pending: 'default',
};

export const CommunicationStatusTag: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation('notifications');
  return <Tag color={STATUS_COLOR[status] || 'default'}>{t(`status.${status}`, status)}</Tag>;
};

interface CommunicationDetailProps {
  record: CommunicationLogDto | null;
  open: boolean;
  onClose: () => void;
}

const CommunicationDetail: React.FC<CommunicationDetailProps> = ({ record, open, onClose }) => {
  const { t } = useTranslation(['notifications', 'common']);

  return (
    <Drawer
      title={t('notifications:comms.detailTitle')}
      open={open}
      onClose={onClose}
      width={640}
    >
      {record && (
        <>
          <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label={t('notifications:log_page.recipient')}>
              {record.userName ? `${record.userName} (${record.recipient})` : record.recipient}
            </Descriptions.Item>
            <Descriptions.Item label={t('notifications:log_page.eventType')}>
              {t(`notifications:eventTypes.${record.eventType}`, record.eventType)}
            </Descriptions.Item>
            <Descriptions.Item label={t('notifications:log_page.channel')}>
              {t(`notifications:channels.${record.channel}`, record.channel)}
            </Descriptions.Item>
            <Descriptions.Item label={t('notifications:log_page.status')}>
              <CommunicationStatusTag status={record.status} />
            </Descriptions.Item>
            <Descriptions.Item label={t('notifications:log_page.sentAt')}>
              {new Date(record.createdAt).toLocaleString()}
            </Descriptions.Item>
            {record.subject && (
              <Descriptions.Item label={t('notifications:log_page.subject')}>
                {record.subject}
              </Descriptions.Item>
            )}
          </Descriptions>

          {record.status === 'failed' && record.errorMessage && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('notifications:comms.errorLabel')}
              description={record.errorMessage}
            />
          )}

          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            {t('notifications:comms.messageBody')}
          </Text>
          {record.content ? (
            record.channel === 'email' ? (
              // Sandboxed (no scripts, no same-origin) so stored email HTML
              // cannot execute or reach the app context.
              <iframe
                title={t('notifications:comms.messageBody')}
                sandbox=""
                srcDoc={record.content}
                style={{ width: '100%', height: 360, border: '1px solid #f0f0f0', borderRadius: 4 }}
              />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                {record.content}
              </pre>
            )
          ) : (
            <Text type="secondary">{t('notifications:comms.noContent')}</Text>
          )}
        </>
      )}
    </Drawer>
  );
};

export default CommunicationDetail;
